import { Debug, Physics } from '@react-three/cannon';
import { OrbitControls } from '@react-three/drei';
import { Canvas } from '@react-three/fiber';
import { memo, useMemo, useRef, useState, useCallback, useEffect } from 'react';
import { Box3, Mesh, Vector2, Vector3 } from 'three';
import { useEventListener, useLocalStorage } from 'usehooks-ts';
import { FaQuestionCircle } from "react-icons/fa";
import { Modal, Button } from "react-bootstrap";

import { useTheme } from '../contexts/ThemeContext';
import { ComboConfig, getNewSizeAndPositionOnComboStreak } from '../features/combos';
import { useStatistics } from '../features/stats';
import { themes } from '../features/themes';
import { config, magicValues } from '../shared/constants';
import { LocalStorageKeys } from '../shared/LocalStorageKeys';
import { round } from '../tools/round';
import { BaseTile } from './BaseTile';
import { CameraController } from './CameraController';
import { ConditionalWrapper } from './ConditionalWrapper';
import { DirLight } from './DirLight';
import { FadingTiles } from './FadingTile';
import { GameEnding } from './GameEnding';
import { Greeting } from './Greeting';
import { MovingTile } from './MovingTile';
import { PerfectEffectProps, PerfectEffects } from './PerfectEffect';
import { Score } from './Score';
import { ReactTile } from './Tile';
import { useInitVisitInSession } from '../features/firstVisitInSession';
import { useMinimumActionInterval } from '../hooks/useMinimumActionInterval';
import originalGreetingImage from '../images/original_greeting.png';
import useUserInfo from '../../../../Hooks/useUserInfo';
import { useAudio } from '../../../../Providers/AudioProvider';
import Task from '../../Task';
import { userGameRewards } from '../../../../apis/user';
import logo from '../../../../assets/images/coinlogo.png';

// This will be updated dynamically in the component

const gameConfig = {
  physics: {
    gravityDown: 600,
    bounciness: 0.1,
  },
  // Added fixed values that were previously controlled by Leva
  speedOfMovingTile: 200,
  debugPhysics: false,
};

export function Games({ autoplay }) {
  const { theme } = useTheme();
  const { userDetails, updateUserInfo, updatewatchScreenInfo, watchScreen } = useUserInfo();
  const playClick = useAudio();
  const [showInstructions, setShowInstructions] = useState(false);
  const [apiCallMade, setApiCallMade] = useState(false);
  const [showGameOver, setShowGameOver] = useState(false);
  const [playButtonDisabled, setPlayButtonDisabled] = useState(false);
  const [finalScore, setFinalScore] = useState(0);
  const [apiHighScore, setApiHighScore] = useState(0);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 720);

  // Create dynamic tile size based on screen size
  const tileSize = isDesktop ? 80 : 100;
  const defaultPreviousTile = {
    position: new Vector3(0, 0, 0),
    size: new Vector2(tileSize, tileSize),
  };

  const pointsPerStack = 10;

  const IsPointsReached =
    watchScreen?.LoginMessage ===
    "You have already reached the maximum rewards limit"
      ? true
      : false;

  const {
    thisGameStats,
    globalStats,
    updateAllStatsOnCutBox,
    updateAllStatsOnGameStart,
    updateAllStatsOnLose,
    updateAllStatsOnBeatingHighScore,
    resetThisGameStats,
  } = useStatistics();
  useInitVisitInSession();

  // Use values from gameConfig instead of Leva controls
  const speedOfMovingTile = gameConfig.speedOfMovingTile;
  const debugPhysics = gameConfig.debugPhysics;

  const debug = window.location.search.includes('debug');

  const [isStarted, setIsStarted] = useState(false);
  const [isEnded, setIsEnded] = useState(false);
  const [isEnding, setIsEnding] = useState(false);
  const [index, setIndex] = useState(0);
  const [highScore, setHighScore] = useLocalStorage(LocalStorageKeys.HighScore, index);
  const [isHighScoreNew, setIsHighScoreNew] = useState(false);
  if (index > highScore) {
    // TODO! don't update in render (https://reactjs.org/link/setstate-in-render)
    // Can be tested by removing `highScore` localStorage entry, dropping the box once, and looking in the console. It will have a related warning with a stack trace pointing here.
    if (!isHighScoreNew) {
      setIsHighScoreNew(true);
      updateAllStatsOnBeatingHighScore();
    }
    setHighScore(index);
  }
  const [staticTiles, setStaticTiles] = useState([]);
  const [fadingTiles, setFadingTiles] = useState([]);
  const [effects, setEffects] = useState([]);

  const movingTileMeshRef = useRef(null);

  const previousTile = staticTiles.at(-1) ?? defaultPreviousTile;

  //#region Performance Optimization — prevents rendering old tiles.
  // TODO render only visible tiles, and zoom out smoothly on game end so that the player would not wait too see the full zoom out, so that his device is not overloaded with the rendering. Currently, on game end, all the tiles that were hidden become visible, which can put a great deal of stress on the user's device if his score is very high like 5000.
  const shouldSliceForOptimization = isStarted && !isEnded;
  /**
   * I'm letting this much show because some users may play from a device like iPad Pro 12.9", in that mode when the screen is split between two applications, and that's when even 30 tiles would not be enough.
   *
   * It does not really matter how much if it's less than 100, because most devices today can hold up to 1000 tiles rendered simultaneously.
   */
  const howMuchToShowAfterCroppingForOptimization = 40;
  const staticTilesPossiblySliced = shouldSliceForOptimization
    ? staticTiles.slice(-howMuchToShowAfterCroppingForOptimization)
    : staticTiles;
  const fadingTilesPossiblySliced = shouldSliceForOptimization
    ? fadingTiles.slice(-howMuchToShowAfterCroppingForOptimization)
    : fadingTiles;
  /** @todo Probably, the effects would not suffer if they're always sliced, no matter the game condition. But slicing them is overall not necessary, since the old ones are filtered out inside the `PerfectEffects` function later. */
  const effectsPossiblySliced = shouldSliceForOptimization
    ? effects.slice(-howMuchToShowAfterCroppingForOptimization)
    : effects;
  //#endregion

  // Define handleApiResponse with useCallback
  const handleApiResponse = useCallback(async (points) => {
    // Set flag to indicate API call is being made
    setApiCallMade(true);
    
    updateUserInfo((prev) => ({
      ...prev,
      isLoading: true,
    }));
    try {
      const apiData = {
        gamePoints: points,
        gameType: "stack",
      };
      const res = await userGameRewards(apiData);
      if (res) {
        updateUserInfo((prev) => ({
          ...prev,
          isLoading: false,
        }));
        updatewatchScreenInfo((prev) => ({
          ...prev,
          LoginMessage: res?.message,
        }));
        
        // Extract high score from API response
        const stackGame = res?.user?.gameRewards?.games?.find(game => game.gameType === "stack");
        if (stackGame && stackGame.highScore) {
          setApiHighScore(stackGame.highScore);
        }
      }
    } catch (error) {
      console.error("API call failed:", error);
      updateUserInfo((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  }, [updateUserInfo, updatewatchScreenInfo]);

  // API call only when game is over
  useEffect(() => {
    const makeApiCall = async () => {
      if (isEnded && !IsPointsReached && !apiCallMade && finalScore > 0) {
        setIsEnding(true); // Keep the screen blocked during API call
        const points = finalScore * pointsPerStack;
        
        try {
          await handleApiResponse(points);
          
          // After API call is complete, show the Game Over popup
          setShowGameOver(true);
          setPlayButtonDisabled(true);
          setIsEnding(false);
          
          // Enable the Play Again button after 500ms
          setTimeout(() => {
            setPlayButtonDisabled(false);
          }, 500);
        } catch (error) {
          console.error("API call failed:", error);
          // Still show the Game Over popup even if API call fails
          setShowGameOver(true);
          setPlayButtonDisabled(true);
          setIsEnding(false);
          
          setTimeout(() => {
            setPlayButtonDisabled(false);
          }, 500);
        }
      }
    };
    
    makeApiCall();
  }, [isEnded, IsPointsReached, apiCallMade, finalScore, handleApiResponse, pointsPerStack]);

  // Listen for window resize to update tile sizes dynamically
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 720);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch initial high score on component mount
  useEffect(() => {
    const fetchInitialHighScore = async () => {
      try {
        const res = await userGameRewards({ gamePoints: 0, gameType: "stack" });
        if (res && res.user && res.user.gameRewards && res.user.gameRewards.games) {
          const stackGame = res.user.gameRewards.games.find(game => game.gameType === "stack");
          if (stackGame && stackGame.highScore) {
            setApiHighScore(stackGame.highScore);
          }
        }
      } catch (error) {
        console.error("Failed to fetch initial high score:", error);
      }
    };
    
    fetchInitialHighScore();
  }, []);

  function cutBox() {
    if (!movingTileMeshRef.current) return;

    // calculate previous and current tiles centers
    const currentTile = new Box3().setFromObject(movingTileMeshRef.current);
    const currentCenter = new Vector3();
    movingTileMeshRef.current.localToWorld(currentCenter);
    currentTile.getCenter(currentCenter);

    // get vectors difference
    const diff = currentCenter.clone().sub(previousTile.position);

    const absDiffX = round(Math.abs(diff.x), 14); // JS messes up floating point calculation, which results in `x` or `z` being e.g. 3.552713678800501e-15 instead of 0. If it is not zero when it should be, the box will spawn in a physically impossible position and start glitching, or spawn one-sided. That's why I'm rounding to a precision that a human would not distinguish, but which rounds values really close to 0 to plain 0.
    const absDiffZ = round(Math.abs(diff.z), 14);

    const newSize = previousTile.size.clone();
    newSize.x -= absDiffX;
    newSize.y -= absDiffZ;

    if (newSize.x < 0 || newSize.y < 0) {
      lose();

      setFadingTiles((prev) => {
        if (!movingTileMeshRef.current) return prev;
        return [
          ...prev,
          {
            position: movingTileMeshRef.current.position,
            size: previousTile.size,
            index,
          },
        ];
      });
      return;
    }

    // calculate absolute error
    const errorX = absDiffX / previousTile.size.x;
    const errorZ = absDiffZ / previousTile.size.y;

    const errorPercentage = errorX + errorZ;

    /** if error is less than arbitrary epsilon than don't cut the tile at all */
    const eps = 0.05;
    const isConsideredPerfect = errorPercentage <= eps;
    if (isConsideredPerfect) {
      diff.x = 0;
      diff.z = 0;
      newSize.x += absDiffX;
      newSize.y += absDiffZ;

      spawnEffect(previousTile.position, previousTile.size);
    } else {
      const cutSizeX = previousTile.size.x - newSize.x;
      const cutSizeZ = previousTile.size.y - newSize.y;

      const signX = currentCenter.x - previousTile.position.x < 0 ? -1 : 1;
      const signZ = currentCenter.z - previousTile.position.z < 0 ? -1 : 1;

      const position = new Vector3(
        cutSizeX ? currentCenter.x + (signX * newSize.x) / 2 : previousTile.position.x,
        previousTile.position.y + config.tileHeight,
        cutSizeZ ? currentCenter.z + (signZ * newSize.y) / 2 : previousTile.position.z,
      );

      setFadingTiles((prev) => [
        ...prev,
        // Cut tile
        {
          position,
          size: new Vector2(cutSizeX || previousTile.size.x, cutSizeZ || previousTile.size.y),
          index,
        },
      ]);
    }

    const newCenter = new Vector3(
      previousTile.position.x + diff.x / 2,
      previousTile.position.y + config.tileHeight,
      previousTile.position.z + diff.z / 2,
    );

    const newStaticTile = {
      position: newCenter,
      size: newSize,
      index,
    };

    /** "possibly" since it is not confirmed until `isConsideredPerfect` is checked. */
    const possiblyNewCurrentCombo = thisGameStats.currentCombo + 1;
    const isComboStreak =
      isConsideredPerfect && possiblyNewCurrentCombo > ComboConfig.streak.startAfter;
    const newOrEnlargedStaticTile = isComboStreak
      ? {
          ...newStaticTile,
          ...getNewSizeAndPositionOnComboStreak(defaultPreviousTile, newStaticTile),
          createdAt: Date.now(),
        }
      : newStaticTile;

    setStaticTiles((prev) => [...prev, newOrEnlargedStaticTile]);

    updateAllStatsOnCutBox(errorPercentage, isConsideredPerfect);
  }

  function moveUp() {
    setIndex((prev) => prev + 1);
  }

  function spawnEffect(position, size) {
    const newCurrentCombo = thisGameStats.currentCombo + 1;
    setEffects((prev) => [
      ...prev,
      {
        position,
        size,
        materialOpacity: 1,
        currentCombo: newCurrentCombo,
      },
    ]);
  }

  function lose() {
    setIsEnded(true);
    setIsEnding(true);
    setFinalScore(index);
    // Show ending animation for 1.5 seconds
    setTimeout(() => {
      // If points limit is reached, show game over immediately
      if (IsPointsReached) {
        setShowGameOver(true);
        setIsEnding(false);
      }
      // If score is 0, show game over immediately (no API call needed)
      else if (index === 0) {
        setShowGameOver(true);
        setIsEnding(false);
      }
      // Otherwise, the API call will trigger showing the game over
    }, 1500);
    setIndex((prev) => prev - 1);
    updateAllStatsOnLose();
  }

  function reset() {
    setIsEnded(false);
    setIsStarted(false);
    setIsEnding(false);
    setIndex(0);
    setFadingTiles([]);
    setStaticTiles([]);
    setEffects([]);
    setIsHighScoreNew(false);
    setShowGameOver(false);
    setApiCallMade(false);
    setPlayButtonDisabled(false);
    resetThisGameStats();
  }

  const { preventActionOrPrepareAndContinue } = useMinimumActionInterval(50);

  function act() {
    if (preventActionOrPrepareAndContinue()) return;

    // Don't allow any actions while in the ending state
    if (isEnding) return;

    if (isEnded) {
      reset();
      return;
    }
    if (!isStarted) {
      setIsStarted(true);
      updateAllStatsOnGameStart();
      return;
    }
    cutBox();
    moveUp();
  }

  useEventListener('keydown', (event) => {
    if (event.repeat) return;
    if (event.code === 'Space') {
      act();
    }
  });

  function exitToMenu() {
    playClick();
    updateUserInfo((prev) => ({
      ...prev,
      isPlay: false,
      currentComponent: Task,
      currentComponentText: "task",
      lastComponent: userDetails?.currentComponent,
      lastComponentText: userDetails?.currentComponentText,
      isMenu: true,
      menuCount: userDetails?.menuCount + 1,
    }));
  }

  function closePopUp() {
    playClick();
    reset();
  }

  return (
    <div style={{ 
      height: '100vh', 
      width: '100vw', 
      position: 'relative',
    }}>
      {/* Transparent overlay that blocks all interactions during ending animation */}
      {isEnding && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'transparent',
          zIndex: 9999,
          cursor: 'not-allowed',
          pointerEvents: 'all',
        }} />
      )}
      
      {/* Game Header with Back Button and Question Mark */}
      <div className="game-header-simon d-flex justify-content-between align-items-center p-2" 
           style={{ position: 'absolute', top: 0, left: 0, width: '100%', zIndex: 10, display: 'flex', justifyContent: 'space-between', padding: '8px' }}>
        <img
          className="cancel-button"
          onClick={exitToMenu}
          src="https://d1ccopf5cw1hft.cloudfront.net/s3data/cancelicon.svg"
          style={{ width: "25px", cursor: 'pointer', pointerEvents:"auto" }}
          alt="Cancel"
        />
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div 
            style={{ pointerEvents:"auto", color: "#03bb14", marginLeft: '8px', cursor: 'pointer' }}
            onClick={() => {
              playClick();
              setShowInstructions(!showInstructions);
            }}
          >
            <FaQuestionCircle size={24} />
          </div>
        </div>
      </div>

      {/* Instructions Modal using Bootstrap Modal component */}
      <Modal
        style={{ maxHeight: "100vh", background: 'black', maxWidth: '100vw' }}
        show={showInstructions}
        onHide={() => {
          playClick();
          setShowInstructions(false);
        }}
        centered
        className="instructions-modal"
      >
        <Modal.Header>
          <h4 style={{
            marginTop: '10px',
            background: 'linear-gradient(180deg, #FD8D23 -1.34%, #FAE61C 10.31%, #EEF73B 20.95%, #4CF038 42.23%, #33C6F6 65.04%, #7475FF 80.74%, #D457FF 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>HOW TO PLAY</h4>
          <button
            type="button"
            className="btn-close"
            onClick={() => setShowInstructions(false)}
            style={{
              background: `url('https://d1ccopf5cw1hft.cloudfront.net/s3data/Xicon.png') no-repeat center center`,
              backgroundSize: '24px 24px',
              width: '24px',
              height: '28px',
              border: 'none',
              opacity: '1',
              padding: '0',
              pointerEvents: 'fill',
              zIndex: '1000',
            }}
          >
          </button>
        </Modal.Header>
        <Modal.Body>
          <div className="instructions-content">
            <ol className="instruction-list" style={{ fontSize: "0.9rem" }}>
              <li>TAP THE SCREEN TO START</li>
              <li>TAP AT THE RIGHT MOMENT TO DROP THE TILE</li>
              <li>TRY TO ALIGN EACH NEW TILE WITH THE ONE BELOW</li>
              <li>BUILD THE HIGHEST TOWER YOU CAN</li>
              <li>IF A TILE DOESN'T ALIGN AT ALL, THE GAME ENDS</li>
            </ol>
            <div>
              <h5>PERFECT STACK TIPS:</h5>
              <ul style={{ fontSize: "0.9rem" }}>
                <li>IF YOU ALIGN PERFECTLY, NO PART OF THE TILE WILL BE CUT</li>
                <li>CHAINS OF PERFECT STACKS WILL GROW YOUR TILE SIZE</li>
              </ul>
            </div>
            <div className="token-info">
              <h5>TOKEN REWARDS:</h5>
              <ul className="reward-list" style={{ fontSize: "0.9rem" }}>
                <li>EARN 10 $MEMETV TOKENS FOR EACH SUCCESSFUL STACK</li>
              </ul>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* Game Over Modal - Updated to show API high score */}
      {showGameOver && !IsPointsReached && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          width: '100%', 
          height: '100%', 
          backgroundColor: 'rgba(0,0,0,0.7)', 
          zIndex: 20,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          pointerEvents: "auto"
        }}>
          <div style={{ 
            backgroundColor: '#333', 
            color: 'white', 
            padding: '20px', 
            borderRadius: '10px',
            maxWidth: '80%',
            textAlign: 'center',
            pointerEvents: "auto"
          }}>
            <h3 style={{ margin: '0 0 15px 0' }}>GAME OVER!</h3>
            
            <p>YOUR SCORE: {finalScore}</p>
            <p>BEST:{apiHighScore > 0 ? apiHighScore : highScore}</p>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '10px 0' }}>
              <img src={logo} alt="Token" style={{ width: '25px', marginRight: '10px' }} />
              <p style={{ margin: 0 }}>{finalScore * pointsPerStack}{' '} TOKENS</p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '15px' }}>
              <button 
                onClick={closePopUp}
                disabled={playButtonDisabled}
                style={{ 
                  backgroundColor: playButtonDisabled ? '#6c95c8' : '#007bff', 
                  color: 'white', 
                  border: 'none', 
                  padding: '8px 16px', 
                  borderRadius: '4px',
                  cursor: playButtonDisabled ? 'not-allowed' : 'pointer',
                  pointerEvents: playButtonDisabled ? "none" : "auto"
                }}
              >
                {playButtonDisabled ? 'LOADING...' : 'PLAY AGAIN'}
              </button>
              <button 
                onClick={exitToMenu}
                style={{ 
                  backgroundColor: '#6c757d', 
                  color: 'white', 
                  border: 'none', 
                  padding: '8px 16px', 
                  borderRadius: '4px',
                  cursor: 'pointer',
                  pointerEvents: "auto"
                }}
              >
                EXIT
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Daily Limit Modal */}
      {showGameOver && IsPointsReached && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          width: '100%', 
          height: '100%', 
          backgroundColor: 'rgba(0,0,0,0.7)', 
          zIndex: 20,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          pointerEvents: "auto"
        }}>
          <div style={{ 
            backgroundColor: '#333', 
            color: 'white', 
            padding: '20px', 
            borderRadius: '10px',
            maxWidth: '80%',
            textAlign: 'center',
            pointerEvents: "auto"
          }}>
            <h3>DAILY LIMIT REACHED</h3>
            <p>YOU'VE REACHED THE MAXIMUM REWARDS LIMIT FOR TODAY</p>
            <button 
              onClick={exitToMenu}
              style={{ 
                backgroundColor: '#6c757d', 
                color: 'white', 
                border: 'none', 
                padding: '8px 16px', 
                borderRadius: '4px',
                cursor: 'pointer',
                marginTop: '15px',
                pointerEvents: "auto"
              }}
            >
              EXIT TO GAMES
            </button>
          </div>
        </div>
      )}

      <Canvas
      style={{width: '100vw', height: '100vh'}}
        gl={{ antialias: false, alpha: false }}
        camera={{
          near: 1,
          position: [-250, 250 + magicValues.pointOfViewFix, -250],
        }}
        orthographic
        {...(autoplay
          ? {
              onClick: act,
            }
          : {
              onPointerDown: act,
            })}
        shadows="basic"
      >
        <color attach="background" args={[autoplay ? '#1f0014' : theme.background(index)]} />
        <CameraController
          previousTile={previousTile}
          isStarted={isStarted}
          isEnded={isEnded}
          index={index}
        />
        <ambientLight color="#ccc" intensity={0.9} />
        <DirLight previousTile={previousTile} />
        {isStarted && !isEnded && (
          <MovingTile
            index={index}
            movingTileMeshRef={movingTileMeshRef}
            previousTile={previousTile}
            autoplay={autoplay}
            lastCube={staticTiles.at(-1)}
            speedOfMovingTile={speedOfMovingTile}
          />
        )}
        <Physics
          gravity={[
            0,
            -gameConfig.physics.gravityDown,
            0,
          ]}
          defaultContactMaterial={{
            restitution: gameConfig.physics.bounciness,
          }}
        >
          <ConditionalWrapper condition={debugPhysics} Wrapper={Debug}>
            <BaseTile tileSize={tileSize} />
            {staticTilesPossiblySliced.map((tile, tileArrIndex) => (
              <ReactTile
                key={tile.index}
                {...tile}
                prevSize={staticTilesPossiblySliced[tileArrIndex - 1]?.size}
              />
            ))}
            <FadingTiles fadingTiles={fadingTilesPossiblySliced} />
          </ConditionalWrapper>
        </Physics>
        <PerfectEffects effects={effectsPossiblySliced} setEffects={setEffects} />
        {debug && <OrbitControls target={previousTile.position} />}
        {/* TODO think about pre-loading everything. In reality, I suppose, it just makes all the objects wait for each other to finish loading before starting the scene and rendering. This makes the BaseTile look like it does not flash on load, but adds a significant amount of time to the first appearance of all of the Canvas. */}
        {/* <Preload all /> */}
      </Canvas>
      
      {/* Add CSS styles for the modal */}
      <style jsx="true">{`
        .instructions-modal {
          background: black;
        }
        
        .instructions-modal .modal-content {
          background-color: #333;
          color: white;
        }
        
        .instructions-modal .modal-header {
          border-bottom: 1px solid #444;
        }
        
        .instructions-modal .modal-title {
          color: white;
          font-weight: bold;
        }
        
        .instructions-modal .btn-close {
          color: white;
          filter: invert(1) grayscale(100%) brightness(200%);
        }
        
        .instructions-modal ol, 
        .instructions-modal ul {
          padding-left: 20px;
          margin-bottom: 15px;
        }
        
        .token-info {
          margin-top: 15px;
        }

        /* Stacker-specific styles */
        .instructions-modal-stacker .modal-content {
          background: linear-gradient(135deg, #5609b4, #225a5a);
          border: 1px solid #30cfd0;
          box-shadow: 0 0 15px rgba(48, 207, 208, 0.6);
        }
        
        .instructions-modal-stacker .modal-header {
          border-bottom: 1px solid #30cfd0;
          background-color: rgba(51, 8, 103, 0.7);
        }
        
        .instructions-modal-stacker h5 {
          color: #30cfd0;
          text-shadow: 0 0 5px rgba(48, 207, 208, 0.5);
        }
      `}</style>
      
      <UILayerMemoized
        {...{
          index,
          isStarted,
          isEnded,
          isEnding,
          isHighScoreNew,
        
          thisGameStats,
          globalStats,
          apiHighScore,
        }}
      />
    </div>
  );
}

const UILayerMemoized = memo(UILayer);

function UILayer({
  index,
  isStarted,
  isEnded,
  isEnding,
  isHighScoreNew,
  
  thisGameStats,
  globalStats,
  apiHighScore,
}) {
  return (
    <>
      <GreetingMemoized index={index} isStarted={isStarted} />
      <Score index={index} isEnded={isEnded} />
      <GameEndingMemoized
        isStarted={isStarted}
        isEnded={isEnded}
        isEnding={isEnding}
        isHighScoreNew={isHighScoreNew}
        thisGameStats={thisGameStats}
        globalStats={globalStats}
        apiHighScore={apiHighScore}
      />
      {/* {displayOriginalGameImages && (
        <img
          src={originalGreetingImage}
          alt="Original game greeting"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: 375,
            opacity: 0.5,
            pointerEvents: 'none',
          }}
        />
      )} */}
    </>
  );
}

const GreetingMemoized = memo(Greeting);
const GameEndingMemoized = memo(GameEnding); 